import { onThemeChange } from '../theme';

/**
 * CanvasComponent — shared lifecycle for the visualizers.
 *
 * - DPR-aware sizing
 * - ResizeObserver-driven re-fit
 * - Theme-aware: re-reads tokens on theme change so traces/grids update live
 * - Subclass overrides draw() (called once per RAF when running)
 */
export abstract class CanvasComponent {
  readonly el: HTMLCanvasElement;
  protected readonly ctx: CanvasRenderingContext2D;

  protected w = 0;
  protected h = 0;
  protected dpr = 1;

  private animId: number | null = null;
  private ro: ResizeObserver | null = null;
  private offThemeChange: (() => void) | null = null;

  constructor() {
    this.el = document.createElement('canvas');
    this.el.className = 'canvas-component';
    const ctx = this.el.getContext('2d');
    if (!ctx) throw new Error('2D context unavailable');
    this.ctx = ctx;
  }

  protected attachLifecycle(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(() => this.fit());
      this.ro.observe(this.el);
    } else {
      window.addEventListener('resize', () => this.fit());
    }
    this.offThemeChange = onThemeChange(() => this.onThemeChanged());
  }

  protected fit(): void {
    const rect = this.el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    this.dpr = dpr;
    this.el.width = Math.floor(rect.width * dpr);
    this.el.height = Math.floor(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.w = rect.width;
    this.h = rect.height;
    this.onResized();
  }

  protected onResized(): void {
    /* hook for subclasses */
  }

  /** Called when the user switches theme; subclasses can re-cache colors here. */
  protected onThemeChanged(): void {
    /* hook */
  }

  start(): void {
    if (this.animId !== null) return;
    this.fit();
    const tick = () => {
      this.draw();
      this.animId = requestAnimationFrame(tick);
    };
    this.animId = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.animId !== null) cancelAnimationFrame(this.animId);
    this.animId = null;
  }

  destroy(): void {
    this.stop();
    this.ro?.disconnect();
    this.ro = null;
    this.offThemeChange?.();
    this.offThemeChange = null;
  }

  protected abstract draw(): void;
}
